import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CachedBuildingService,
  DynamicScriptLoader,
  ENVIRONMENT,
  Environment,
  SharedService,
  UserService
} from '@cfp/core';
import { ImageUploadButton, InputDatepicker } from '@cfp/ui';
import { LoadingSpinner } from '@cfp/ui/common';
import { AlertModalResult, AlertService } from '@cfp/ui/feedback';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { TranslateModule } from '@ngx-translate/core';
import { BuildingReportDTO, BuildingReportsService } from 'csr-asset-app';
import { ImageResult } from 'ngx-image2dataurl';
import { Observable, combineLatest } from 'rxjs';
import { filter, skip } from 'rxjs/operators';
import { MAX_LENGTH_BUILDING_REPORT_DESCRIPTION } from '../../consts';

declare var ClassicEditor: any;

@Component({
  selector: 'cfp-building-report-properties',
  templateUrl: './building-report-properties.html',
  styleUrls: ['./building-report-properties.scss'],
  imports: [LoadingSpinner, InputDatepicker, CKEditorModule, ReactiveFormsModule, ImageUploadButton, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuildingReportProperties implements OnInit {
  protected readonly environment = inject<Environment>(ENVIRONMENT);
  private readonly userService = inject(UserService);
  private readonly sharedService = inject(SharedService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly alertService = inject(AlertService);
  private readonly dynamicScriptLoader = inject(DynamicScriptLoader);
  private readonly buildingReportsService = inject(BuildingReportsService);
  private readonly cachedBuildingService = inject(CachedBuildingService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly buildingId = input<number | undefined>(undefined);
  public readonly buildingReportId = input<number | undefined>(undefined);

  // Constants / one-time config (not signals — never change after init)
  protected readonly buildingReportDescriptionMaxLength = MAX_LENGTH_BUILDING_REPORT_DESCRIPTION;
  protected readonly useHtmlEditor = this.environment.components.consultantReportUseHtmlEditor !== false;
  protected readonly editorConfig = {
    toolbar: ['bold', 'italic', 'underline', '|', 'subscript', 'superscript', '|', 'link'],
    removePlugins: [
      'Heading',
      'Title',
      'Autoformat',
      'BlockQuote',
      'Table',
      'TableToolbar',
      'List',
      'Indent',
      'IndentBlock'
    ],
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://'
    }
  };
  protected showDeleteButton: boolean = false; // Fixed hidden, can this be removed?

  protected readonly isLoading = signal(true);
  protected readonly isLoadingEditor = signal(false);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly buildingReport = signal<BuildingReportDTO | undefined>(undefined);
  protected readonly editor = signal<any | undefined>(undefined);
  protected readonly form = signal<FormGroup | undefined>(undefined);
  protected readonly partnershipLogoAsBase64 = signal<string | undefined>(undefined);
  protected readonly partnershipLogoAsFile = signal<File | undefined>(undefined);

  private readonly editorInstance = signal<any | undefined>(undefined);

  constructor() {
    if (this.useHtmlEditor) {
      this.loadScript();
    }

    effect(() => {
      const editorInstance = this.editorInstance();
      if (!editorInstance) {
        return;
      }

      const buildingReport = this.buildingReport();
      if (!buildingReport) {
        return;
      }

      editorInstance.setData(buildingReport.description || '');
    });

    combineLatest([toObservable(this.buildingId), toObservable(this.buildingReportId)])
      .pipe(
        skip(1),
        filter(([buildingId, buildingReportId]) => !!buildingId && !!buildingReportId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isLoading.set(true);
        this.getReport();
      });
  }

  public ngOnInit(): void {
    this.getReport();
  }

  protected onEditorReady(editorInstance: any): void {
    this.editorInstance.set(editorInstance);
  }

  protected onEditorChange(event: { editor: { getData(): string } }): void {
    this.form()?.get('description')?.setValue(event.editor.getData());
  }

  public isFieldValid(field: string): boolean {
    return this.form()?.get(field)?.valid === true;
  }

  public showValidation(field: string, errorName: string, notErrorNames?: string[]): boolean {
    const form = this.form();
    const errors = form?.get(field)?.errors;
    const formErrors = form?.errors;
    return (
      ((errors && errors[errorName]) || (formErrors && formErrors[errorName] === true)) &&
      (!notErrorNames ||
        !notErrorNames.some((notErrorName: string) => {
          return (formErrors && formErrors[notErrorName]) || (errors && errors[notErrorName]);
        }))
    );
  }

  public delete(): void {
    const buildingId = this.buildingId();
    const buildingReportId = this.buildingReportId();
    const buildingReport = this.buildingReport();
    if (!buildingId || !buildingReportId || !buildingReport) {
      return;
    }
    this.alertService
      .showConfirmation('warning', 'consultantreport.confirmDelete.title', 'consultantreport.confirmDelete.body')
      .subscribe({
        next: (result: AlertModalResult) => {
          if (result !== AlertModalResult.CONFIRM) {
            return;
          }

          this.isLoading.set(true);
          this.errorMessage.set(undefined);

          this.buildingReportsService.v2BuildingsBuildingIdReportsIdDelete(buildingId, buildingReportId).subscribe({
            next: () => {
              this.buildingReport.set(undefined);
              this.init(buildingReport);

              this.sharedService.emitDeleteLocalCache();
              this.sharedService.emitReloadComponent();
            },
            error: () => {
              this.errorMessage.set('error.general');
              this.isLoading.set(false);
            }
          });
        }
      });
  }

  public userPartnershipLogoDeleted(): void {
    this.partnershipLogoAsFile.set(undefined);
    this.partnershipLogoAsBase64.set(undefined);
  }

  public userPartnershipLogoSelected(imageResult: ImageResult): void {
    this.partnershipLogoAsFile.set(imageResult.file);
    this.partnershipLogoAsBase64.set(URL.createObjectURL(imageResult.file));
  }

  public save(): void {
    const buildingId = this.buildingId();
    const buildingReportId = this.buildingReportId();
    if (!buildingId || !buildingReportId) {
      return;
    }
    this.isLoading.set(true);
    this.errorMessage.set(undefined);

    const buildingReport: BuildingReportDTO = this.form()?.value as BuildingReportDTO;
    if (buildingReport.description) {
      buildingReport.description = buildingReport.description.trim();
    }
    buildingReport.buildingId = buildingId;

    this.createOrUpdateReport(buildingId, buildingReport, buildingReportId).subscribe({
      next: (report: BuildingReportDTO) => {
        this.init(report);

        this.sharedService.emitDeleteLocalCache();
        this.sharedService.emitReloadComponent();
      },
      error: () => {
        this.errorMessage.set('error.general');
        this.isLoading.set(false);
      }
    });
  }

  private getReport(): void {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }
    const buildingReportId = this.buildingReportId();
    if (buildingReportId) {
      this.cachedBuildingService
        .v2BuildingsBuildingIdReportsIdGet(buildingId, buildingReportId)
        .subscribe((buildingReport: BuildingReportDTO) => {
          this.init(buildingReport);
          this.loadPartnershipLogo(buildingReport);
        });
    } else {
      this.userService.get().subscribe((user) => {
        if (!user) {
          return;
        }
        this.init({
          consultant: { username: user.username },
          checkDate: new Date(),
          buildingId: buildingId
        });
      });
    }
  }

  private init(buildingReport: BuildingReportDTO): void {
    this.buildingReport.set(buildingReport);
    this.isLoading.set(false);
    this.buildForm();
  }

  private loadPartnershipLogo(buildingReport: BuildingReportDTO): void {
    this.userPartnershipLogoDeleted();
    if (buildingReport.hasPartnershipLogo && buildingReport.buildingId && buildingReport.id) {
      this.buildingReportsService
        .v2BuildingsBuildingIdReportsIdPartnershiplogoImageGet(buildingReport.buildingId, buildingReport.id)
        .subscribe({
          next: (partnershipLogoFile: Blob) => {
            this.partnershipLogoAsBase64.set(URL.createObjectURL(partnershipLogoFile));
            this.partnershipLogoAsFile.set(
              new File([partnershipLogoFile], 'no_name', {
                type: partnershipLogoFile.type
              })
            );
          }
        });
    }
  }

  private createOrUpdateReport(
    buildingId: number,
    report: BuildingReportDTO,
    buildingReportId?: number
  ): Observable<BuildingReportDTO> {
    const partnershipLogoAsFile = this.partnershipLogoAsFile();
    if (buildingReportId) {
      return this.buildingReportsService.v2BuildingsBuildingIdReportsIdPut(
        buildingId,
        buildingReportId,
        report,
        undefined,
        partnershipLogoAsFile
      );
    } else {
      return this.buildingReportsService.v2BuildingsBuildingIdReportsPost(
        buildingId,
        report,
        undefined,
        partnershipLogoAsFile
      );
    }
  }

  private buildForm(): void {
    const buildingReport = this.buildingReport();
    this.form.set(
      this.formBuilder.group({
        checkDate: [buildingReport?.checkDate || '', [Validators.required]],
        description: [
          buildingReport?.description || '',
          [Validators.maxLength(this.buildingReportDescriptionMaxLength)]
        ],
        showCfpLogo: [
          buildingReport
            ? buildingReport?.showCfpLogo || this.environment.components.consultantReportDefaultShowCfpLogo
            : this.environment.components.consultantReportDefaultShowCfpLogo,
          []
        ],
        showEnergeticRegionLogo: [buildingReport?.showEnergeticRegionLogo || false, []]
      })
    );
  }

  private loadScript(): void {
    this.isLoadingEditor.set(true);
    this.dynamicScriptLoader.loadScript('assets/js/ckeditor.js').then(() => {
      this.editor.set(ClassicEditor);
      this.isLoadingEditor.set(false);
    });
  }
}
